import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useSWRConfig } from 'swr';
import { API_SERVER } from '../config/env';
import { getAuthToken } from '../services/authSession';

const SOCKET_URL = typeof API_SERVER === 'string' ? API_SERVER.trim().replace(/\/+$/, '') : '';

/**
 * Socket da importação por planilha do clube.
 * Evento `imports:update` traz o registro de import (KPIs) no payload.
 *
 * @param {number|string|null} clubId
 * @param {{
 *   onUpdate?: (payload: object) => void,
 *   enabled?: boolean,
 *   revalidateSWR?: boolean,
 * }} [options]
 */
export function useClubImportsSocket(clubId, options = {}) {
    const { onUpdate, enabled = true, revalidateSWR = true } = options;
    const { mutate } = useSWRConfig();
    const [connected, setConnected] = useState(false);
    const onUpdateRef = useRef(onUpdate);
    const debounceRef = useRef(null);

    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    const room = clubId ? `imports:club:${clubId}` : null;

    const flushUpdate = useCallback(
        payload => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                if (revalidateSWR) {
                    mutate(
                        key => typeof key === 'object' && key?.url === 'club/subscribers/import',
                        undefined,
                        { revalidate: true },
                    );
                }
                onUpdateRef.current?.(payload);
            }, 200);
        },
        [mutate, revalidateSWR],
    );

    useEffect(() => {
        if (!enabled || !SOCKET_URL || !room) {
            setConnected(false);
            return undefined;
        }

        const token = getAuthToken();
        if (!token) {
            setConnected(false);
            return undefined;
        }

        const socket = io(SOCKET_URL, {
            auth: { token },
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 12,
            reconnectionDelay: 1500,
        });

        const onConnect = () => {
            setConnected(true);
            socket.emit('join:room', room);
        };
        const onDisconnect = () => setConnected(false);
        const onImportUpdate = payload => {
            if (payload?.clubId && Number(payload.clubId) !== Number(clubId)) return;
            flushUpdate(payload);
        };

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('imports:update', onImportUpdate);
        if (socket.connected) onConnect();

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            socket.emit('leave:room', room);
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('imports:update', onImportUpdate);
            socket.disconnect();
            setConnected(false);
        };
    }, [clubId, enabled, room, flushUpdate]);

    return { connected, room };
}

export default useClubImportsSocket;
